/* Harber story video: <story-video> plays a muted loop only when it is useful.
 * - The <video> (MP4) is rendered in the DOM with preload="none" so the iOS app scraper can read its src,
 *   and nothing is downloaded until playback starts.
 * - Embeds (Vimeo / YouTube) sit in a <template> and are injected on play.
 * - Desktop (>= 750px, no reduced motion, no Save-Data) autoplays when the video scrolls into view;
 *   mobile shows the poster and waits for a tap. Scrolling away pauses; the toggle button pauses/resumes.
 */
(() => {
  if (customElements.get('story-video')) return;
  const desktop = matchMedia('(min-width: 750px)');
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);

  class StoryVideo extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.video = this.querySelector('video');
      this.btn = this.querySelector('[data-play]');
      this.btn?.addEventListener('click', () => (this.playing ? this.pause(true) : this.play(true)));
      if (!('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver((entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (visible) {
          this.poster();
          if (this.resume || this.wantsAutoplay()) this.play(false);
        } else if (this.playing) {
          this.resume = true;
          this.pause(false);
        }
      }, { rootMargin: '120px 0px' });
      this.io.observe(this);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.ready = false;
    }

    wantsAutoplay() {
      return this.hasAttribute('autoplay') && !this.userPaused && desktop.matches && !reduce.matches && !saveData();
    }

    play(byUser) {
      if (byUser) this.userPaused = false;
      this.resume = false;
      if (this.video) {
        if (this.video.preload === 'none') this.video.preload = 'auto';
        const p = this.video.play();
        if (p && p.catch) p.catch(() => this.state(false));
      } else if (!this.frame) {
        const tpl = this.querySelector('template');
        if (!tpl) return;
        this.frame = tpl.content.firstElementChild.cloneNode(true);
        this.appendChild(this.frame);
      }
      this.state(true);
    }

    pause(byUser) {
      if (byUser) {
        this.userPaused = true;
        this.resume = false;
      }
      if (this.video) this.video.pause();
      else if (this.frame) {
        this.frame.remove();
        this.frame = null;
      }
      this.state(false);
    }

    state(on) {
      this.playing = on;
      this.classList.toggle('is-playing', on);
      if (!this.btn) return;
      this.btn.setAttribute('aria-pressed', String(on));
      const label = on ? this.btn.dataset.labelPause : this.btn.dataset.labelPlay;
      if (label) this.btn.setAttribute('aria-label', label);
    }

    /* Vimeo embeds without a poster image: fetch the public oEmbed thumbnail once, when in view. */
    poster() {
      const id = this.dataset.vimeoId;
      if (!id || this.posterDone || this.querySelector('[data-poster]')) return;
      this.posterDone = true;
      fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${id}`)}&width=1600`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!d || !d.thumbnail_url) return;
          const img = new Image();
          img.src = d.thumbnail_url;
          img.alt = '';
          img.decoding = 'async';
          img.className = 'story-video__poster';
          img.setAttribute('data-poster', '');
          img.addEventListener('load', () => img.classList.add('is-in'), { once: true });
          this.prepend(img);
        })
        .catch(() => {});
    }
  }
  if (!customElements.get('story-video')) customElements.define('story-video', StoryVideo);
})();
